import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Subscription} from 'rxjs/Subscription';
import {Headers} from '@angular/http';
import {PhoneNumber, PhoneNumberService} from './phone-number.service';
import {isNullOrUndefined} from 'util';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {List} from 'lodash';

/**\
 * Service that holds the user information
 */
@Injectable()
export class UserService {

  readonly billingApiPath = '/api/call_event/v1/billing/';

  public static TOKEN_HEADER = 'token';
  public static CHANGE_PASS_FLAG = 'user.authentication.changepass';

  userSubject = new BehaviorSubject<User>(null);
  // TODO: Make distinctUntilChanged comparison
  currentUser = this.userSubject;
  loginStatus = new BehaviorSubject<LoginStatus>(null);
  backendStatus = new BehaviorSubject<BackendStatus>(null);
  backendStatusText = new BehaviorSubject<string>('Erro ao comunicar com o Backend');

  isRoot: Observable<boolean>;
  isAdmin: Observable<boolean>;

  userSubscription: Subscription = null;

  static userNeedToChangePass(u: User): boolean {
    const needChangePass = u.options[UserService.CHANGE_PASS_FLAG];
    return (needChangePass && needChangePass === 'true')
  }

  getBackendStatus() {
    const start: Date = new Date();
    this.httpClient.get<any>('/api')
      .map((res) => {
        console.log(`Received Backend Status response:${JSON.stringify(res)}`)
        return new BackendStatus(200, res.version, res.appName, 0);
      })
      .catch((res: HttpErrorResponse) => {
        console.log(`Received Error:'${JSON.stringify(res)}'`);
        return Observable.of(new BackendStatus(res.status, '', '', 0, res.error.message));
      }).subscribe((bs: BackendStatus) => {
        if (bs) {
          const stop = new Date();
          console.log(`totalTime: ${stop.getTime() - start.getTime()}`);
          bs.latency = stop.getTime() - start.getTime();
          this.backendStatus.next(bs);
          if (bs.status !== 200) {
            this.backendStatusText.next(`Erro de status do Backend:${bs.status}`);
          } else {
            this.backendStatusText.next(`${bs.appName} ${bs.version}`);
          }
        } else {
          this.backendStatus.next(null);
          this.backendStatusText.next('Erro ao comunicar com o Backend')
        }
      }, (err) => {
        console.log(`Error trying to get the backend status:${err.error}`);
        this.backendStatus.next(null);
      }
    );
  }

  subscribeUser(username: string, token: string): void {
    this.findById(username).subscribe((u: User) => {
      if (u) {
        this.userSubject.next(u);
        if (UserService.userNeedToChangePass(u)) {
          this.loginStatus.next(new LoginStatus(UserStatus.NEED_CHANGE_PASS, token));
        } else {
          this.loginStatus.next(new LoginStatus(UserStatus.LOGGED, token));
        }
        this.userSubscription = Observable.interval(60000).flatMap((i: number) => {
          return this.findById(username);
        }).subscribe((user: User) => {
          if (user) {
            this.userSubject.next(user);
          } else {
            this.userSubject.next(null);
          }
        }, (error) => {
          console.log(`Error trying to get the User: ${error}`);
          if (error === 'TOKEN_NOT_FOUND') {
            localStorage.removeItem('token');
          }
          this.userSubject.next(null);
        });
      } else {
        console.log(`Error trying to get the User...`);
        this.userSubject.next(null);
      }
      console.log(`User Phones:${u.getPhones()}`);
    });
  }

  login(username: string, password: string) {
    this.loginStatus.next(new LoginStatus(UserStatus.TRYING_LOGIN));
    const headers = new HttpHeaders()
      .append('Authorization', 'Basic ' + btoa(username + ':' + password));
    this.httpClient.get<any>('/api/users/get_token', {headers: headers})
      .map(res => {
        return res.token;
      })
      .subscribe((token) => {
        localStorage.setItem('token', token);
        localStorage.setItem('username', username);
        // this.loginStatus.next(new LoginStatus(UserStatus.LOGGED, token));
        console.log(`User logged with token:${token}`);
        this.subscribeUser(username, token);
      }, (error) => {
        this.handleGetTokenError(error);
      });
    return;
  }

  logout() {
    this.httpClient.put<any>('/api/users/logout/' + localStorage.getItem('username'), null)
      .map(res => {
        console.log('Received: ' + res);
      }).subscribe(() => {
      console.log('User successfully logged out...');
    }, (response) => {
      console.log(`Got error trying to logout: ${response.error}`);
    });
    localStorage.removeItem('token');
    if (this.userSubscription && !this.userSubscription.closed) {
      this.userSubscription.unsubscribe();
    }
    this.loginStatus.next(new LoginStatus(UserStatus.LOGGED_OUT, 'USER_LOGOUT'));
  }

  handleGetTokenError(error: HttpErrorResponse) {
    console.log(`Error trying to login, Status:${error} Body:${JSON.stringify(error)}`);
    const ls = new LoginStatus(UserStatus.ERROR, error.error.message);
    if (ls.message === 'USER_STILL_LOGGED_ERROR') {
      ls.hosts = error.error.registers;
    }
    this.loginStatus.next(ls);
  }

  getAuthHeaders(): Headers {
    const headers = new Headers();
    headers.append('token', localStorage.getItem('username') + ':' + localStorage.getItem('token'));
    // console.log(`Headers:${JSON.stringify(headers)}`);
    return headers;
  }

  hasRole(role: string): boolean {
    return this.currentUser.getValue().roles.indexOf(role) >= 0;
  }

  getAll(): Observable<Array<User>> {
    return this.httpClient.get<Array<User>>('/api/users')
      .map((users: Array<User>) => users.map((u: User) => User.fromJSON(u)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'));
  };

  findById(id: string): Observable<User> {
    return this.httpClient.get<User>('/api/users/' + id)
      .map((u: User) => User.fromJSON(u))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  verifyExists(id: string): Observable<boolean> {
    return this.httpClient.get<any>('/api/users/check/' + id)
      .map(res => res.id ? true : false)
      .catch((response: HttpErrorResponse) => {
        if (response.status === 404 && response.error.message === 'USER_NOT_FOUND') {
          return Observable.of(false);
        }
        console.log(`Received error:${JSON.stringify(response)}`);
        return Observable.throw(response.message || 'Server error');
      });
  };

  save(u: User): Observable<User> {
    return this.httpClient.put<User>('/api/users', u)
      .map((user: User) => User.fromJSON(user))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'));
  }

  update(u: User): Observable<User> {
    return this.httpClient.post<User>('/api/users', u)
      .map((user: User) => User.fromJSON(user))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'));
  }

  remove(id: string): Observable<string> {
    return this.httpClient.delete<any>('/api/users/' + id)
      .map(res => res.id)
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'));
  }

  changePassword(u: User, newPassword: string, needChange: boolean): Observable<ChangePasswordResponse> {
    return this.httpClient.post<ChangePasswordResponse>('/api/users/change_pass/' + u.username,
      new ChangePasswordRequest(newPassword, '' + needChange)
    )
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
  }

  setBilling(u: User, status: boolean): Observable<string> {
    let client: Observable<any>;
    if (status) {
      client = this.httpClient
        .post<any>(this.billingApiPath + 'enable', {username: u.username});
    } else {
      client = this.httpClient
        .post<any>(this.billingApiPath + 'disable', {username: u.username});
    }
    return client.map(res => res.id);
  }

  getBillingUser(userId: string): Observable<BillingUser> {
    return this.httpClient.get<BillingUser>(this.billingApiPath + 'user_id/' + userId)
      .map((u: BillingUser) => BillingUser.fromJSON(u))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'));
  }

  getAllBillingUsers(customerId: string): Observable<Array<BillingUser>> {
    return this.httpClient.get<Array<BillingUser>>(this.billingApiPath + 'customer/' + customerId)
      .map((users: Array<BillingUser>) => users.map((u: BillingUser) => BillingUser.fromJSON(u)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'));
  }

  constructor(private httpClient: HttpClient) {
    const username = localStorage.getItem('username');
    const token = localStorage.getItem('token');
    if (username && token) {
      this.subscribeUser(username, token);
    } else {
      this.getBackendStatus();
    }
    this.currentUser.subscribe((u: User) => console.log(`User: ${JSON.stringify(u)}`));
    this.isRoot = this.currentUser.map((user: User) => user.hasRole('ROOT'));
    this.isAdmin = this.currentUser.map((user: User) => user.hasRole('ROOT') || user.hasRole('USER_ADMIN'));
  };
}

export class LoginStatus {
  constructor(public status: UserStatus, public message?: string, public hosts?: UserClient[]) {
  };
}

export const enum UserStatus {
  LOGGED,
  LOGGED_OUT,
  TRYING_LOGIN,
  ERROR,
  NEED_CHANGE_PASS
}

export class UserClient {
  constructor(public host: string, public agent: string) {
  };
}

export class User {

  static fromJSON(json: any): User {
    console.log(`Received JSON: ${JSON.stringify(json)}`);
    const object: User = Object.create(User.prototype);
    Object.assign(object, json);
    return object;
  }

  getPhones(): Array<PhoneNumber> {
    console.log(`phones:${JSON.stringify(this.phones)}`);
    const returnValue = new Array<PhoneNumber>();
    for (const i in this.phones) {
      if (this.phones.hasOwnProperty(i)) {
        // console.log(`Converting ${i}: ${JSON.stringify(this.phones[i])}`);
        const pn = PhoneNumberService.parse(this.phones[i]);
        if (isNullOrUndefined(pn)) {
          console.error(`error trying to convert to Phone Number:${JSON.stringify(this.phones[i])}`);
        } else {
          pn.description = i;
          returnValue.push(pn);
        }
      }
    }
    return returnValue;
  }

  hasRole(role: string): boolean {
    return this.roles.indexOf(role) >= 0;
  }

  hasAnyRole(roles: Array<string>): boolean {
    return this.roles.some(role => roles.indexOf(role) >= 0)
  }

  constructor(public username: string, public password: string, public customerId: string, public name: string,
              public businessRole: string, public roles: Array<string>, public enabled: boolean,
              public licenses: Map<string, string>, public phones: Object, public options: Object,
              public site: string, public identifier: string, public expiration?: number,
              public databaseId?: string, public customerDatabaseId?: string, public message?: string) {
  };
}

export class BackendStatus {
  constructor(public status: number, public version: string, public appName: string, public latency: number,
              public message?: string) {
  }
}

export interface ChangePasswordResponse {
  id: string
}

export class ChangePasswordRequest {
  constructor(public password: string, public needChangePassword: string) {
  }
}

export class BillingUser {

  static fromJSON(json: any): BillingUser {
    console.log(`Received JSON: ${JSON.stringify(json)}`);
    const object: BillingUser = Object.create(BillingUser.prototype);
    Object.assign(object, json);
    return object;
  }

  constructor(public id: string, public userId: string, public username: string, public name: string, public enableBilling: boolean, public customerId) {
  };
}



// WEBPACK FOOTER //
// /Users/tabelini/development/jvm/tenacity/pysix/PysixCloud/src/app/user.service.ts